import { Injectable } from '@angular/core';
import {
  Database,
  ref,
  runTransaction,
  push,
  databaseInstance$,
} from '@angular/fire/database';
import { get, child } from '@angular/fire/database';

@Injectable({
  providedIn: 'root',
})
export class BookingLockService {
  constructor(private db: Database) {}

  /**
   * Attempts to lock a vehicle for a specific date range.
   * Uses a transaction to ensure FCFS.
   */
  async checkAndLockVehicle(
    vehicleId: string,
    startDate: Date,
    endDate: Date,
    userId: string,
  ): Promise<boolean> {
    const locksRef = ref(this.db, `vehicle_locks/${vehicleId}`);

    // Simple date overlap check helper
    const doDatesOverlap = (
      start1: number,
      end1: number,
      start2: number,
      end2: number,
    ) => {
      return start1 <= end2 && end1 >= start2;
    };

    const requestedStart = startDate.getTime();
    const requestedEnd = endDate.getTime();

    try {
      const result = await runTransaction(locksRef, (currentLocks: any) => {
        if (currentLocks === null) {
          // No locks exist at all, create one
          return {
            [userId]: {
              startDate: requestedStart,
              endDate: requestedEnd,
              timestamp: Date.now(),
            },
          };
        } else {
          // Check for overlaps with existing locks
          // We convert the object of locks into an array to iterate
          const locksArray = Object.values(currentLocks) as any[];

          for (const lock of locksArray) {
            // If we find an overlap, we abort the transaction by returning undefined (or same value)
            // signal abort? returning undefined aborts.
            if (
              doDatesOverlap(
                requestedStart,
                requestedEnd,
                lock.startDate,
                lock.endDate,
              )
            ) {
              return; // Abort: overlap found
            }
          }

          // No overlap found, add our lock
          // We use a random key or userId if unique per vehicle per time (simpler to just push a new key if we structure differently,
          // but here we are modifying the 'vehicle_locks/{vehicleId}' object directly.
          // Let's generate a unique key for this lock entry to avoid overwriting same user's other future bookings if needed?
          // Actually, 'push' ID is better, but inside transaction we work with the data snapshot.

          // Let's use a composite key or just a random ID.
          // Simple way: just add to the object.
          const newLockId = `lock_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

          return {
            ...currentLocks,
            [newLockId]: {
              startDate: requestedStart,
              endDate: requestedEnd,
              bookedBy: userId,
              timestamp: Date.now(),
            },
          };
        }
      });

      // If committed is true, it means we replaced the data (or created it), implying success.
      return result.committed;
    } catch (error) {
      console.error('Transaction failed: ', error);
      return false;
    }
  }

  /**
   * Sends a real-time notification to the vendor.
   */
  async sendNotificationToVendor(vendorId: string, notification: any) {
    return this.sendNotification(vendorId, notification);
  }

  /**
   * Sends a real-time notification to any user (Vendor or Customer).
   */
  async sendNotification(userId: string, notification: any) {
    const notifsRef = ref(this.db, `notifications/${userId}`);
    await push(notifsRef, {
      ...notification,
      timestamp: Date.now(),
      read: false,
    });
  }

  /**
   * Fetches current real-time locks for a vehicle.
   * Returns an array of {start, end} intervals.
   */
  async getLockedDates(
    vehicleId: string,
  ): Promise<{ start: number; end: number }[]> {
    const locksRef = ref(this.db, `vehicle_locks/${vehicleId}`);
    const snapshot = await get(locksRef);

    if (!snapshot.exists()) return [];

    const locks = snapshot.val();
    const intervals: { start: number; end: number }[] = [];

    Object.values(locks).forEach((lock: any) => {
      if (lock.startDate && lock.endDate) {
        intervals.push({
          start: lock.startDate,
          end: lock.endDate,
        });
      }
    });

    return intervals;
  }
  /**
   * Removes a lock for a specific booking (e.g. when rejected/cancelled).
   * Matches based on vehicleId, dates, and userId since we don't have lockId.
   */
  async unlockVehicle(
    vehicleId: string,
    startDate: Date,
    endDate: Date,
    userId: string,
  ): Promise<void> {
    const locksRef = ref(this.db, `vehicle_locks/${vehicleId}`);
    const requestedStart = startDate.getTime();
    const requestedEnd = endDate.getTime();

    console.log(`[UnlockVehicle] Attempting to unlock ${vehicleId}`);
    console.log(
      `[UnlockVehicle] Criteria: Start=${requestedStart}, End=${requestedEnd}, User=${userId}`,
    );

    try {
      await runTransaction(locksRef, (currentLocks: any) => {
        if (!currentLocks) {
          console.log('[UnlockVehicle] No locks found for this vehicle.');
          return null;
        }

        console.log(
          '[UnlockVehicle] Current locks:',
          JSON.stringify(currentLocks),
        );

        const updatedLocks = { ...currentLocks };
        let lockFound = false;

        Object.keys(updatedLocks).forEach((key) => {
          const lock = updatedLocks[key];

          // Relaxed Match: Check if dates are within 2000ms (2s) tolerance
          const startDiff = Math.abs(lock.startDate - requestedStart);
          const endDiff = Math.abs(lock.endDate - requestedEnd);

          // Match User ID (or if legacy lock has no bookedBy, assume match if dates are close?)
          // Better to stay safe and require userId if present.
          const isUserMatch = lock.bookedBy === userId || !lock.bookedBy;

          if (isUserMatch && startDiff < 2000 && endDiff < 2000) {
            console.log(
              `[UnlockVehicle] Match found for key: ${key}. Removing lock.`,
            );
            delete updatedLocks[key];
            lockFound = true;
          } else {
            console.log(
              `[UnlockVehicle] No match for key ${key}: UserMatch=${isUserMatch}, StartDiff=${startDiff}, EndDiff=${endDiff}`,
            );
          }
        });

        return lockFound ? updatedLocks : undefined;
      });
    } catch (error) {
      console.error('Unlock transaction failed: ', error);
    }
  }
}
